import React from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  IconButton,
  Skeleton,
  TextField,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ThumbUpAlt from "@mui/icons-material/ThumbUpAlt";
import ThumbUpAltOutlined from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAlt from "@mui/icons-material/ThumbDownAlt";
import ThumbDownAltOutlined from "@mui/icons-material/ThumbDownAltOutlined";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import { tokens } from "../../theme";
import {
  useGetLatestBriefQuery,
  useGetFeedbackQuery,
  useSubmitLikeMutation,
  useSubmitCommentMutation,
  useDeleteLikeMutation,
} from "../../Services/revops-brief";
import {
  BriefItem,
  BriefFeedback,
  RevOpsBrief,
  SubmitLikePayload,
  SubmitCommentPayload,
} from "../../Models/RevOpsBrief";

import { useAskRevOptica } from "Components/AskRevOptica";
import { useAppSelector } from "State/hooks";
import { useGetMeQuery } from "Services/user";

// ─── Data hooks ───────────────────────────────────────────────────────────────

/** Latest brief for the current tenant; withheld until the profile matches it. */
export function useLatestBrief() {
  const companyId = useAppSelector(s => s.company.companyId);
  const { data: profile, isError: profileError, refetch: retryProfile } = useGetMeQuery();
  const ready = !!companyId && profile?.companyId === companyId;
  const { currentData, isLoading, isFetching, isError, refetch } = useGetLatestBriefQuery(companyId, { skip: !ready });
  return {
    companyId,
    profile: ready ? profile : undefined,
    ready,
    brief: ready ? currentData : undefined,
    profileError,
    retryProfile,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
}

export interface BriefFeedbackBundle {
  feedback: BriefFeedback | undefined;
  onLike: (payload: SubmitLikePayload) => void;
  onAddComment: (payload: SubmitCommentPayload) => void;
  onDeleteLike: (payload: { item_id: string; brief_date: string }) => void;
  isCommentLoading: boolean;
  briefDate: string;
  hasError: boolean;
  retry: () => void;
}

export function useBriefFeedback(companyId: string | undefined, brief: RevOpsBrief | null | undefined): BriefFeedbackBundle {
  const { currentData: feedback, isError: feedbackError, refetch: retryFeedback } = useGetFeedbackQuery(`${companyId}|${brief?.date ?? ""}`, {
    skip: !brief?.date,
  });
  const [submitLike, { isError: likeError }] = useSubmitLikeMutation();
  const [submitComment, { isLoading: isCommentLoading, isError: commentError }] = useSubmitCommentMutation();
  const [deleteLike, { isError: deleteError }] = useDeleteLikeMutation();
  return {
    feedback,
    onLike: submitLike,
    onAddComment: submitComment,
    onDeleteLike: deleteLike,
    isCommentLoading,
    briefDate: brief?.date ?? "",
    hasError: !!(feedbackError || likeError || commentError || deleteError),
    retry: () => { if (brief?.date) retryFeedback(); },
  };
}

// ─── Per-item feedback ────────────────────────────────────────────────────────

/**
 * Reactions and comments for one brief item. Returns the inline icon controls
 * and the expandable details separately so callers can place them compactly.
 * Items without a stable id (historical briefs) get no controls.
 */
export function useItemFeedback(item: BriefItem, section: string, fb: BriefFeedbackBundle) {
  const { feedback, onLike, onAddComment, onDeleteLike, isCommentLoading, briefDate } = fb;
  const [showComposer, setShowComposer] = React.useState(false);
  const [commentText, setCommentText] = React.useState("");
  const [reactionModalOpen, setReactionModalOpen] = React.useState(false);
  const [pendingLiked, setPendingLiked] = React.useState<boolean | null>(null);
  const [reactionComment, setReactionComment] = React.useState("");

  const currentLike = item.id
    ? feedback?.likes.find((l) => l.item_id === item.id)
    : undefined;
  const isUp = currentLike?.liked === true;
  const isDown = currentLike?.liked === false;
  const itemComments = item.id
    ? (feedback?.comments.filter((c) => c.item_id === item.id) ?? [])
    : [];

  const handleSubmitComment = () => {
    if (!commentText.trim() || !item.id) return;
    onAddComment({
      item_id: item.id,
      content: commentText.trim(),
      brief_date: briefDate,
      section,
      item_text: item.text,
    });
    setCommentText("");
    setShowComposer(false);
  };

  const handleThumbClick = (liked: boolean) => {
    if (!item.id) return;
    const isActive = currentLike?.liked === liked;
    if(isActive) {
      onDeleteLike({ item_id: item.id, brief_date: briefDate });
      return;
    }
    setPendingLiked(liked);
    setReactionComment("");
    setReactionModalOpen(true);
  };
  const handleReactionSubmit = () => {
    if (!reactionComment.trim() || !item.id || pendingLiked === null) return;
    onLike({
      item_id: item.id,
      liked: pendingLiked,
      content: reactionComment.trim(),
      brief_date: briefDate,
      section,
      item_text: item.text,
    });
    setReactionModalOpen(false);
    setPendingLiked(null);
    setReactionComment("");
  };

  const handleReactionCancel = () => {
    setReactionModalOpen(false);
    setPendingLiked(null);
    setReactionComment("");
  };

  if (!item.id) return { controls: null, details: null };

  const iconSx = { fontSize: 16 };
  const controls = (
    <Box className="brief-feedback" sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
      <IconButton
        size="small"
        aria-label="Like item"
        aria-pressed={isUp}
        onClick= {() => handleThumbClick(true)}
        sx={{ p: 0.5, color: isUp ? "#2563EB" : "#9CA3AF" }}
      >
        {isUp ? <ThumbUpAlt sx={iconSx} /> : <ThumbUpAltOutlined sx={iconSx} />}
      </IconButton>
      <IconButton
        size="small"
        aria-label="Dislike item"
        aria-pressed={isDown}
        onClick={() => handleThumbClick(false)}
        sx={{ p: 0.5, color: isDown ? "#DC2626" : "#9CA3AF" }}
      >
        {isDown ? <ThumbDownAlt sx={iconSx} /> : <ThumbDownAltOutlined sx={iconSx} />}
      </IconButton>
      <IconButton
        size="small"
        aria-label="Comment on item"
        aria-expanded={showComposer}
        onClick={() => setShowComposer((v) => !v)}
        sx={{ p: 0.5, color: "#9CA3AF" }}
      >
        <Badge
          badgeContent={itemComments.length || null}
          color="primary"
          sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", minWidth: 14, height: 14 } }}
        >
          <ChatBubbleOutline sx={iconSx} />
        </Badge>
      </IconButton>
    </Box>
  );

  const details = (
    <>
      {itemComments.length > 0 && (
        <Box sx={{ mt: 1, pl: 1, borderLeft: "2px solid #E5E7EB" }}>
          {itemComments.map((c, i) => (
            <Typography key={i} sx={{ fontSize: 14, color: "#6B7280", py: 0.2, overflowWrap: "anywhere" }}>
              {c.content}
            </Typography>
          ))}
        </Box>
      )}

      {showComposer && (
        <Box sx={{ mt: 1, display: "flex", gap: 0.75, alignItems: "center" }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Add a note..."
            inputProps={{ "aria-label": "Note on this item" }}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmitComment();
              }
            }}
            disabled={isCommentLoading}
            sx={{ "& .MuiInputBase-input": { fontSize: 14, py: 0.75 } }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmitComment}
            disabled={!commentText.trim() || isCommentLoading}
            sx={{ minWidth: 0, whiteSpace: "nowrap" }}
          >
            Save
          </Button>
        </Box>
      )}

      <Dialog open={reactionModalOpen} onClose={handleReactionCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem" }}>Please leave a comment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            placeholder="Add your comment..."
            value={reactionComment}
            onChange={(e) => setReactionComment(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReactionCancel}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReactionSubmit}
            disabled={!reactionComment.trim()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return { controls, details };
}

export const askContext = (briefDate: string, section: string, item: BriefItem) =>
  `Brief date: ${briefDate}\nSection: ${section}\n${item.text}`;

/** Small text action that opens Ask RevOptica with the item as an editable draft. */
export function AskAboutButton({ item, section, briefDate }: { item: BriefItem; section: string; briefDate: string }) {
  const ask = useAskRevOptica();
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={() => ask(askContext(briefDate, section, item))}
      sx={{ py: 0.25, px: 1.25, fontSize: 13, minWidth: 0, whiteSpace: "nowrap" }}
    >
      Ask about this
    </Button>
  );
}

// ─── Full brief sections ──────────────────────────────────────────────────────

function BriefItemRow({ item, section, fb }: { item: BriefItem; section: string; fb: BriefFeedbackBundle }) {
  const { controls, details } = useItemFeedback(item, section, fb);
  return (
    <Box sx={{ py: 1.25, "& + &": { borderTop: "1px solid #EEEBF5" } }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.25 }}>
        {item.category}
      </Typography>
      <Typography sx={{ fontSize: 15, color: "#2A2640", lineHeight: 1.5, overflowWrap: "anywhere" }}>
        {item.text}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
        <AskAboutButton item={item} section={section} briefDate={fb.briefDate} />
        {controls}
      </Box>
      {details}
    </Box>
  );
}

function Section({ title, items, section, fb }: { title: string; items: BriefItem[]; section: string; fb: BriefFeedbackBundle }) {
  return (
    <Box component="section" aria-label={title} sx={{ minWidth: 0 }}>
      <Typography component="h3" sx={{ fontSize: 13, fontWeight: 700, color: "#4B4763", letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: "#9CA3AF", mt: 0.5 }}>None</Typography>
      ) : (
        items.map((item, i) => <BriefItemRow key={item.id ?? `${section}-${i}`} item={item} section={section} fb={fb} />)
      )}
    </Box>
  );
}

/** What Changed, Risks, Opportunities and Recommendations with feedback. */
export function BriefSections({ brief, fb, columns = 1 }: { brief: RevOpsBrief; fb: BriefFeedbackBundle; columns?: 1 | 2 }) {
  const recommendations = brief.recommendations?.length
    ? brief.recommendations
    : (brief.recommended_actions ?? []).map(text => ({ text, category: "Legacy action" }));
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: columns === 2 ? "1fr 1fr" : "1fr" }, gap: 3 }}>
      <Section title="What Changed" items={brief.what_changed ?? []} section="what_changed" fb={fb} />
      <Section title="Risks" items={brief.risks_brief ?? []} section="risks_brief" fb={fb} />
      <Section title="Opportunities" items={brief.opportunities_brief ?? []} section="opportunities_brief" fb={fb} />
      <Section title="Recommendations" items={recommendations} section="recommendations" fb={fb} />
    </Box>
  );
}

export function FeedbackAlert({ fb }: { fb: BriefFeedbackBundle }) {
  if (!fb.hasError) return null;
  return (
    <Typography role="alert" sx={{ fontSize: 14, color: "#9A3412" }}>
      Feedback unavailable or could not be saved. <Button size="small" onClick={fb.retry}>Retry feedback</Button>
    </Typography>
  );
}

export function formatCreatedAt(createdAt?: string): string {
  if (!createdAt) return "Unknown";
  const parsed = new Date(createdAt);
  return Number.isNaN(parsed.getTime())
    ? createdAt
    : parsed.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export const isStaleBrief = (brief: RevOpsBrief, now = new Date()) => brief.date !== localBriefDate(now);

// ─── Insights card ────────────────────────────────────────────────────────────

/** Brief card used on the Insights page. */
export default function TodaysBrief() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { companyId, brief, profileError, retryProfile, isLoading, isError, refetch } = useLatestBrief();
  const fb = useBriefFeedback(companyId, brief);

  if (profileError) return <Box role="alert">Profile unavailable. <Button onClick={() => retryProfile()}>Retry profile</Button></Box>;

  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ p: 3, mt: 2, mx: 2 }}>
        <Skeleton width="30%" height={20} />
        <Skeleton width="100%" height={60} sx={{ mt: 1 }} />
      </Card>
    );
  }

  if (isError) return <Box role="alert">Brief unavailable. Access or subscription restrictions may apply. <Button onClick={() => refetch()}>Retry brief</Button></Box>;
  if (!brief) return <Typography>No revenue brief available yet.</Typography>;

  const summary = meaningfulSummary(brief.summary);
  return (
    <Card variant="outlined" sx={{ overflow: "hidden", p: { xs: 2, md: 3 }, mt: 2, mx: 2, bgcolor: "#FFFFFF" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Typography variant="subtitle1" color={colors.gray[200]} sx={{ fontWeight: 600, bgcolor: colors.gray[900], px: 3, py: 1 }}>
          Today's RevOps Brief
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
          {brief.date} · Created {formatCreatedAt(brief.created_at)} · {isStaleBrief(brief) ? "Not today’s brief — daily data may be stale" : "Today"}
        </Typography>
      </Box>
      <FeedbackAlert fb={fb} />
      {summary && <Typography sx={{ my: 1.5, fontSize: 15 }}>{summary}</Typography>}
      <BriefSections brief={brief} fb={fb} columns={2} />
    </Card>
  );
}
export function localBriefDate(date: Date): string { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; }
export function meaningfulSummary(summary?: string): string | undefined { const value = summary?.trim(); return value && !/^(null|undefined|n\/a|none)$/i.test(value) ? value : undefined; }
